// Fix for the fetchFilters function and useEffect hooks
import React, { useState, useEffect } from "react";
import {
  Search,
  Filter,
  Edit,
  Trash2,
  Plus,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  Loader,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle
} from "lucide-react";
import CreateFilter from "./CreateFilter";
import adminService from "../../../services/api/admin.service";
import { adminUserService } from "@/services/api/admin.user.service";

// Toast Component (same as before)
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, type === "success" ? 5000 : 3000);

    return () => clearTimeout(timer);
  }, [onClose, type]);

  const bgColor = type === "success" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200";
  const textColor = type === "success" ? "text-green-700" : "text-red-700";
  const Icon = type === "success" ? Check : AlertCircle;

  return (
    <div className={`fixed bottom-4 right-4 p-4 border rounded-md shadow-lg flex items-center gap-2 ${bgColor} ${textColor} animate-fadeIn`}>
      <Icon size={20} />
      <span className="text-sm font-medium">{message}</span>
      <button
        onClick={onClose}
        className="ml-2 text-gray-400 hover:text-gray-600"
      >
        <X size={16} />
      </button>
    </div>
  );
};

// Toast Container Component (same as before)
const ToastContainer = ({ toasts, removeToast }) => {
  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          message={toast.message}
          type={toast.type}
          onClose={() => removeToast(toast.id)}
        />
      ))}
    </div>
  );
};

export default function FilterList() {
  const [filters, setFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortConfig, setSortConfig] = useState({
    key: "createdAt",
    direction: "desc",
  });
  const [expandedFilter, setExpandedFilter] = useState(null);
  const [editingFilter, setEditingFilter] = useState(null);
  const [editFormData, setEditFormData] = useState({
    name: "",
    type: "",
    isActive: true,
    criteria: {},
  });
  const [updateLoading, setUpdateLoading] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [showCreateFilter, setShowCreateFilter] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all"); // "all", "active", "inactive"

  // Add a new toast
  const addToast = (message, type = "error") => {
    const id = Date.now();
    setToasts((prevToasts) => [...prevToasts, { id, message, type }]);
  };

  // Remove a toast by id
  const removeToast = (id) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  };

  // FIXED: Single fetchFilters function that handles all scenarios
  const fetchFilters = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use the appropriate API call based on your backend implementation
      // Option 1: If your backend doesn't support status filtering
      const result = await adminService.fetchAllFilters();

      // Option 2: If your backend supports status filtering, uncomment this:
      // const result = await adminService.fetchAllFilters(statusFilter === "all" ? undefined : statusFilter);
      
      if (result.success) {
        setFilters(result.data);
      } else {
        setError(result.message || "Failed to fetch filters");
      }
    } catch (err) {
      setError(
        typeof err === "string" ? err : "Error connecting to the server"
      );
      console.error("Error fetching filters:", err);
    } finally {
      setLoading(false);
    }
  };

  // FIXED: Single useEffect for initial load and status filter changes
  useEffect(() => {
    fetchFilters();
  }, [statusFilter]); // This will run on mount and when statusFilter changes

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
  };

  const sortedFilters = [...filters].sort((a, b) => {
    if (sortConfig.key === "name" || sortConfig.key === "type") {
      if (a[sortConfig.key].toLowerCase() < b[sortConfig.key].toLowerCase()) {
        return sortConfig.direction === "asc" ? -1 : 1;
      }
      if (a[sortConfig.key].toLowerCase() > b[sortConfig.key].toLowerCase()) {
        return sortConfig.direction === "asc" ? 1 : -1;
      }
      return 0;
    } else {
      // For dates
      const dateA = new Date(a[sortConfig.key]);
      const dateB = new Date(b[sortConfig.key]);
      return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
    }
  });

  // Apply status filter along with search
  const filteredFilters = sortedFilters.filter((filter) => {
    // Apply status filter
    if (statusFilter === "active" && !filter.isActive) return false;
    if (statusFilter === "inactive" && filter.isActive) return false;
    
    // Apply search filter
    return (
      filter.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      filter.type.toLowerCase().includes(searchTerm.toLowerCase())
    );
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + " " + date.toLocaleTimeString();
  };

  const toggleExpandFilter = (id) => {
    if (expandedFilter === id) {
      setExpandedFilter(null);
    } else {
      setExpandedFilter(id);
    }
  };

  // Start editing a filter
  const handleEditClick = (filter, e) => {
    e.stopPropagation(); // Prevent row expansion when clicking edit
    setEditingFilter(filter._id);

    // Convert criteria from Map-like object to plain object if needed
    const criteriaObj = {};
    Object.entries(filter.criteria).forEach(([key, values]) => {
      criteriaObj[key] = [...values]; // Create a copy of the array
    });

    setEditFormData({
      name: filter.name,
      type: filter.type,
      isActive: filter.isActive,
      criteria: criteriaObj,
    });

    // Auto-expand the filter being edited
    setExpandedFilter(filter._id);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingFilter(null);
    setUpdateError(null);
    setUpdateSuccess(false);
  };

  // Handle form field changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditFormData({
      ...editFormData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Handle criteria changes
  const handleCriteriaChange = (criteriaKey, index, value) => {
    const updatedCriteria = { ...editFormData.criteria };
    updatedCriteria[criteriaKey][index] = value;
    setEditFormData({
      ...editFormData,
      criteria: updatedCriteria,
    });
  };

  // Add a new criteria value
  const handleAddCriteriaValue = (criteriaKey) => {
    const updatedCriteria = { ...editFormData.criteria };
    updatedCriteria[criteriaKey] = [...updatedCriteria[criteriaKey], ""];
    setEditFormData({
      ...editFormData,
      criteria: updatedCriteria,
    });
  };

  // Remove a criteria value
  const handleRemoveCriteriaValue = (criteriaKey, index) => {
    const updatedCriteria = { ...editFormData.criteria };
    updatedCriteria[criteriaKey] = updatedCriteria[criteriaKey].filter(
      (_, i) => i !== index
    );
    setEditFormData({
      ...editFormData,
      criteria: updatedCriteria,
    });
  };

  // Toggle filter activation status
  const handleToggleActivation = async (filter, e) => {
    e.stopPropagation(); // Prevent row expansion
    await updateFilterData(filter._id, { isActive: !filter.isActive });
  };

  // Submit the form to update the filter
  const handleSubmitUpdate = async (e) => {
    e.preventDefault();
    await updateFilterData(editingFilter, editFormData);
  };

  // API call to update filter
  const updateFilterData = async (filterId, updateData) => {
    setUpdateLoading(true);
    setUpdateError(null);
    setUpdateSuccess(false);

    try {
      const result = await adminUserService.updateFilter(filterId, updateData);

      if (result.success) {
        // Update the filter in the local state
        setFilters((prevFilters) =>
          prevFilters.map((filter) =>
            filter._id === filterId ? result.data : filter
          )
        );
        setUpdateSuccess(true);
        addToast("Filter updated successfully!", "success");

        // If this was a full edit (not just toggling status), exit edit mode
        if (editingFilter === filterId) {
          setTimeout(() => {
            setEditingFilter(null);
            setUpdateSuccess(false);
          }, 2000);
        }
      } else {
        setUpdateError(result.message);
        addToast(result.message);
      }
    } catch (err) {
      const errorMsg = "Unexpected error occurred";
      setUpdateError(errorMsg);
      addToast(errorMsg);
      console.error("Error updating filter:", err);
    } finally {
      setUpdateLoading(false);
    }
  };

  // Handle delete click
  const handleDeleteClick = async (filterId, e) => {
    e.stopPropagation(); // Prevent row expansion

    if (window.confirm("Are you sure you want to delete this filter? This action cannot be undone.")) {
      setDeleteLoading(true);

      try {
        const result = await adminUserService.deleteFilter(filterId);

        if (result.success) {
          // Remove the filter from the local state
          setFilters((prevFilters) =>
            prevFilters.filter((filter) => filter._id !== filterId)
          );
          addToast("Filter deleted successfully!", "success");
        } else {
          addToast(`Failed to delete filter: ${result.message}`);
        }
      } catch (err) {
        console.error("Error deleting filter:", err);
        const errorMessage = "Unexpected error occurred while deleting filter";
        addToast(`Failed to delete filter: ${errorMessage}`);
      } finally {
        setDeleteLoading(false);
      }
    }
  };

  // FIXED: Handler for when a filter is created in the CreateFilter component
  const handleFilterCreated = async () => {
    // Refresh filters list to show the new filter
    await fetchFilters(); 
    setShowCreateFilter(false); // Hide the create form
    addToast("Filter created successfully!", "success");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg font-medium">
          {" "}
          <Loader className="animate-spin text-indigo-500" size={36} />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-lg border border-red-200 text-red-700">
        <p className="font-medium">Error loading filters</p>
        <p>{error}</p>
        <button
          onClick={fetchFilters}
          className="mt-3 px-4 py-2 bg-red-100 hover:bg-red-200 rounded-md transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  // Show the CreateFilter component if showCreateFilter is true
  if (showCreateFilter) {
    return (
      <CreateFilter
        onFilterCreated={handleFilterCreated}
        onCancel={() => setShowCreateFilter(false)}
      />
    );
  }

  return (
    <div className="rounded-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Filters Management</h1>
        <button
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
          onClick={() => setShowCreateFilter(true)}
        >
          <Plus size={18} />
          Add New Filter
        </button>
      </div>

      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative w-72">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Search filters..."
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          {/* Status Filter Dropdown */}
          <div className="flex items-center gap-2">
            <span className="text-gray-500">Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Filters</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-gray-500">Total Filters: {filters.length}</span>
          <Filter size={18} className="text-gray-500" />
        </div>
      </div>

      {/* Rest of your component remains the same... */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white rounded-lg overflow-hidden">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button
                  className="flex items-center gap-1"
                  onClick={() => handleSort("name")}
                >
                  Name
                  {sortConfig.key === "name" &&
                    (sortConfig.direction === "asc" ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    ))}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button
                  className="flex items-center gap-1"
                  onClick={() => handleSort("type")}
                >
                  Type
                  {sortConfig.key === "type" &&
                    (sortConfig.direction === "asc" ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    ))}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button
                  className="flex items-center gap-1"
                  onClick={() => handleSort("createdAt")}
                >
                  Created At
                  {sortConfig.key === "createdAt" &&
                    (sortConfig.direction === "asc" ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    ))}
                </button>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <button
                  className="flex items-center gap-1"
                  onClick={() => handleSort("lastUpdated")}
                >
                  Last Updated
                  {sortConfig.key === "lastUpdated" &&
                    (sortConfig.direction === "asc" ? (
                      <ChevronUp size={16} />
                    ) : (
                      <ChevronDown size={16} />
                    ))}
                </button>
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {filteredFilters.length > 0 ? (
              filteredFilters.map((filter) => (
                <React.Fragment key={filter._id}>
                  <tr
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => toggleExpandFilter(filter._id)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      <div className="flex items-center gap-2">
                        {expandedFilter === filter._id ? (
                          <ChevronDown size={16} />
                        ) : (
                          <ChevronUp size={16} />
                        )}
                        {filter.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {filter.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          filter.isActive
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {filter.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(filter.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(filter.lastUpdated)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex gap-3 justify-end">
                        <button
                          className="text-blue-600 hover:text-blue-800"
                          onClick={(e) => handleEditClick(filter, e)}
                          title="Edit filter"
                        >
                          <Edit size={18} />
                        </button>

                        <button
                          className={`flex items-center gap-1 px-2 py-1 rounded text-sm transition-colors ${
                            filter.isActive
                              ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                              : "bg-green-100 text-green-800 hover:bg-green-200"
                          }`}
                          onClick={(e) => handleToggleActivation(filter, e)}
                          title={filter.isActive ? "Deactivate filter" : "Activate filter"}
                        >
                          {filter.isActive ? (
                            <>
                              <XCircle size={16} />
                       
                            </>
                          ) : (
                            <>
                              <CheckCircle size={16} />
                    
                            </>
                          )}
                        </button>

                        <button
                          className="text-green-600 hover:text-green-800"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpandFilter(filter._id);
                          }}
                          title="View details"
                        >
                          <Eye size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expandedFilter === filter._id && (
                    <tr className="bg-gray-50">
                      <td colSpan={6} className="px-6 py-4">
                        {editingFilter === filter._id ? (
                          <form onSubmit={handleSubmitUpdate}>
                            {/* Your existing edit form code */}
                            <div className="space-y-4">
                              <div className="grid grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Filter Name
                                  </label>
                                  <input
                                    type="text"
                                    name="name"
                                    value={editFormData.name}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                  />
                                </div>
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Filter Type
                                  </label>
                                  <input
                                    type="text"
                                    name="type"
                                    value={editFormData.type}
                                    onChange={handleInputChange}
                                    className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                    required
                                  />
                                </div>
                              </div>

                              <div>
                                <label className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    name="isActive"
                                    checked={editFormData.isActive}
                                    onChange={handleInputChange}
                                    className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                                  />
                                  <span className="text-sm font-medium text-gray-700">
                                    Active
                                  </span>
                                </label>
                              </div>

                              <div>
                                <h3 className="font-medium text-gray-800 mb-2">
                                  Filter Criteria:
                                </h3>
                                {Object.entries(editFormData.criteria).map(
                                  ([key, values]) => (
                                    <div
                                      key={key}
                                      className="mb-4 bg-white p-3 rounded border border-gray-200"
                                    >
                                      <div className="font-medium text-gray-700 mb-2">
                                        {key}:
                                      </div>
                                      <div className="space-y-2">
                                        {values.map((value, idx) => (
                                          <div
                                            key={idx}
                                            className="flex items-center gap-2"
                                          >
                                            <input
                                              type="text"
                                              value={value}
                                              onChange={(e) =>
                                                handleCriteriaChange(
                                                  key,
                                                  idx,
                                                  e.target.value
                                                )
                                              }
                                              className="flex-1 p-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleRemoveCriteriaValue(
                                                  key,
                                                  idx
                                                )
                                              }
                                              className="text-red-600 hover:text-red-800"
                                            >
                                              <X size={16} />
                                            </button>
                                          </div>
                                        ))}
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleAddCriteriaValue(key)
                                          }
                                          className="text-blue-600 hover:text-blue-800 text-sm flex items-center gap-1"
                                        >
                                          <Plus size={14} /> Add Value
                                        </button>
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>

                              {updateError && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
                                  {updateError}
                                </div>
                              )}

                              {updateSuccess && (
                                <div className="p-3 bg-green-50 border border-green-200 rounded-md text-green-700 text-sm flex items-center gap-2">
                                  <Check size={16} />
                                  Filter updated successfully!
                                </div>
                              )}

                              <div className="flex gap-2">
                                <button
                                  type="submit"
                                  disabled={updateLoading}
                                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm transition-colors disabled:bg-blue-400"
                                >
                                  {updateLoading ? "Saving..." : "Save Changes"}
                                </button>
                                <button
                                  type="button"
                                  onClick={handleCancelEdit}
                                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-md text-sm transition-colors"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          </form>
                        ) : (
                          <>
                            <div className="mb-3">
                              <h3 className="font-medium text-gray-800 mb-2">
                                Filter Criteria:
                              </h3>
                              <div className="bg-white p-3 rounded border border-gray-200">
                                {Object.entries(filter.criteria).map(
                                  ([key, values]) => (
                                    <div key={key} className="mb-2">
                                      <span className="font-medium text-gray-700">
                                        {key}:
                                      </span>
                                      <div className="flex flex-wrap gap-2 mt-1">
                                        {values.map((value, idx) => (
                                          <span
                                            key={idx}
                                            className="bg-gray-100 text-gray-800 px-2 py-1 rounded text-sm"
                                          >
                                            {value}
                                          </span>
                                        ))}
                                      </div>
                                    </div>
                                  )
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded text-sm transition-colors"
                                onClick={(e) => handleEditClick(filter, e)}
                              >
                                Edit Filter
                              </button>
                              <button
                                className={`px-3 py-1 rounded text-sm transition-colors ${
                                  filter.isActive
                                    ? "bg-amber-100 text-amber-800 hover:bg-amber-200"
                                    : "bg-green-100 text-green-800 hover:bg-green-200"
                                }`}
                                onClick={(e) =>
                                  handleToggleActivation(filter, e)
                                }
                              >
                                {filter.isActive ? "Deactivate" : "Activate"}
                              </button>

                              <button
                                className="bg-red-100 text-red-800 hover:bg-red-200 px-3 py-1 rounded text-sm transition-colors"
                                onClick={(e) => handleDeleteClick(filter._id, e)}
                              >
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            ) : (
              <tr>
                <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                  No filters found matching your search criteria
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      
      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
}